import React, { useState, useEffect } from "react";
import { Box, styled } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config.js";
import { useTranslation } from "react-i18next";

const ExpandedContent = styled(Box)(({ theme }) => ({
  padding: "16px",
  backgroundColor: "#E8EAEE",
  borderBottom: "1px solid #C8CBD0",
  display: "flex",
  gap: "16px",
  [theme.breakpoints.down("md")]: { flexDirection: "column" },
  "& .vehicle-image": {
    width: "250px",
    height: "180px",
    objectFit: "cover",
    borderRadius: "8px",
    flexShrink: 0,
    [theme.breakpoints.down("md")]: { width: "100%", height: "200px" },
  },
  "& .details-container": { flex: 1, display: "flex", flexDirection: "column", gap: "8px" },
  "& .details-grid": {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    [theme.breakpoints.down("lg")]: { gridTemplateColumns: "repeat(2, 1fr)" },
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  "& .detail-section": { padding: "12px", borderRadius: "8px" },
  "& .section-header": {
    fontSize: "14px",
    fontWeight: 600,
    color: "#fff",
    backgroundColor: "#95A0B3",
    padding: "8px 12px",
    borderRadius: "4px",
    marginBottom: "8px",
  },
  "& .section-content": { display: "flex", flexDirection: "column", gap: "6px" },
  "& .detail-row": { display: "flex", alignItems: "flex-start", gap: "8px" },
  "& .detail-label": { fontWeight: 600, color: "#647490", fontSize: "13px", minWidth: "70px" },
  "& .detail-value": { color: "#333", fontSize: "13px", wordBreak: "break-word" },
  "& .comment-section": { marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" },
  "& .comment-label": { fontWeight: 600, color: "#333", fontSize: "14px" },
}));

export default function ExpandedRowContent({ row, gates = [], onUpdated }) {
  const { t } = useTranslation("");
  const [comment, setComment] = useState(row.comments || row.comment || "");
  const [saving, setSaving] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [row.vehicleImage]);
  useEffect(() => {
    setComment(row.comments || row.comment || "");
  }, [row._logId, row.comments, row.comment]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${config.baseURL}/vehicle-log/${row._logId}/comment`,
        { comments: comment },
        { withCredentials: true }
      );
      toast.success("Comment saved");
      onUpdated?.(row._logId, comment);
    } catch {
      toast.error("Failed to save comment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ExpandedContent>
      {/* Vehicle image */}
      {row.vehicleImage && !imageFailed ? (
        <img
          src={row.vehicleImage}
          alt=""
          className="vehicle-image"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <Box
          className="vehicle-image"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#E5E8EE",
            color: "#647490",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          No image
        </Box>
      )}

      <div className="details-container">
        <div className="details-grid">
          {/* Vehicle Details */}
          <div className="detail-section">
            <div className="section-header">{t("Vehicle Details")}</div>
            <div className="section-content">
              <div className="detail-row">
                <span className="detail-label">{t("Name")}:</span>
                <span className="detail-value">{row.vehicleName || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Reg. No.")}:</span>
                <span className="detail-value">{row.vehicleNumber || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Type")}:</span>
                <span className="detail-value">{row.type || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Entry Details */}
          <div className="detail-section">
            <div className="section-header">{t("Entry Details")}</div>
            <div className="section-content">
              <div className="detail-row">
                <span className="detail-label">{t("Date")}:</span>
                <span className="detail-value">{row.entryDate || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Time")}:</span>
                <span className="detail-value">{row.entryTime || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Gate")}:</span>
                <span className="detail-value">{row.entryGate || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Exit Details */}
          <div className="detail-section">
            <div className="section-header">{t("Exit Details")}</div>
            <div className="section-content">
              <div className="detail-row">
                <span className="detail-label">{t("Date")}:</span>
                <span className="detail-value">{row.exitDate || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Time")}:</span>
                <span className="detail-value">{row.exitTime || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Gate")}:</span>
                <span className="detail-value">{row.exitGate || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Other Details */}
          <div className="detail-section">
            <div className="section-header">{t("Other Details")}</div>
            <div className="section-content">
              <div className="detail-row">
                <span className="detail-label">{t("Duration")}:</span>
                <span className="detail-value">{row.duration || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Driver")}:</span>
                <span className="detail-value">{row.driver || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("Phone")}:</span>
                <span className="detail-value">{row.driverPhone || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Comment section */}
        <div className="comment-section">
          <label className="comment-label">{t("Add Comment")}</label>
          <div style={{ display: "flex", gap: "20px" }}>
            <textarea
              style={{
                flex: 1,
                padding: "10px",
                border: "2px solid #183059",
                borderRadius: "16px",
                fontSize: "13px",
                fontFamily: "inherit",
                resize: "vertical",
                minHeight: "60px",
              }}
              placeholder={t("Enter your comment or flag any issue...")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                alignSelf: "flex-end",
                width: "125px",
                backgroundColor: "#F9E719",
                color: "#1F242E",
                border: "1px solid #414958",
                height: "55px",
                padding: "10px 24px",
                borderRadius: "8px",
                fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: "18px",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "..." : t("Save")}
            </button>
          </div>
        </div>
      </div>
    </ExpandedContent>
  );
}
